import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import {
  SchoolsCrossLink, SchoolsHero, SchoolsResearchFeature, SchoolsAnswer,
  SchoolsMechanism, SchoolsConfidence, SchoolsLanguages, SchoolsHow,
  SchoolsBuilt, SchoolsMethod, SchoolsFirst, SchoolsGov, SchoolsSample,
  SchoolsFAQ, SchoolsFooterCTA,
} from '../components/Schools.jsx';

/* Schools — Italian (route /schools/italian).
 *
 * Derived from routes/SchoolsSpanish.jsx (Aran's 2026-07-09 Spanish
 * draft) per Aran's instruction, with per-language adjustments:
 * language names; mechanism example phrase ("Voglio parlare" —
 * UNCONFIRMED against the course); Languages point 1 credibility
 * line; the Castilian/Mexican variety FAQ dropped (scFaqQ5 absent —
 * the FAQ component skips missing keys). Free-month offer and the
 * heads-of-languages band are identical across the MFL pages.
 */
const TWEAK_DEFAULTS = {
  // --- shared chrome (Header) ---
  navWelshResources: 'Welsh Resources',
  navCommunity: 'Community',
  navAboutUs: 'About us',
  navBlog: 'Blog',
  navContact: 'Contact',
  login: 'Login',
  startLearning: 'Start Learning',

  // --- account-setup destination (per-language slug pending Ivan/Tom) ---
  scSetupUrl: 'https://www.saysomethingin.app/schools1',

  // --- cross-link bar ---
  scCrossText: 'We also offer',

  // --- hero ---
  scKicker: 'Italian for schools',
  scHeadline: 'Your pupils can be genuinely confident Italian speakers.',
  scSub: "All you have to do is give them five minutes a day of this simple classroom tool. No planning, no marking, you don't even need fluent Italian yourself – and you get a full month free to see what a difference it makes.",
  scCta: 'Claim your free month',
  scCta2: 'See how it works',
  scTrust: 'A full month of Italian, free for your whole school. No card needed to start.',
  scBadgeBig: 'A month free',
  scBadgeLabel: 'of Italian, for your whole school – then £15 per teacher a month.',

  // --- Cardiff study band ---
  scRfKicker: 'Independently evaluated – Cardiff University',
  scRfTitle: 'The evidence comes from Wales. The problem is in every language classroom.',
  scRfBody: "In 2024, Cardiff University ran an independent study of SaySomethingin in primary schools in Wales – pupils studying a language in lessons and never speaking it anywhere else. Sound familiar? In the classes without SaySomethingin, almost every pupil scored in the bottom 10% of possible marks. In the classes using it, half the pupils scored in the top 10% – from as little as five minutes a week. And months later, they still remembered what they'd learned.",
  scRfHonest: "The study was in Welsh – but every SaySomethingin course runs on the same engine: the same loop, the same brain science, the same voices in your pupils' ears. We haven't run this in Italian classrooms yet, so we'll be particularly keen to work closely with the first few schools who do.",
  scRfLink: 'Read the published study',
  scRfQuote: 'Some of the most successful learners with SaySomethingin are not the learners usually recognised as academically strong.',
  scRfCite: '– Cardiff University report, 2024',
  scRfStat1Big: '5 minutes',
  scRfStat1Label: 'a week was enough to significantly improve results',
  scRfStat2Big: '1 in 2',
  scRfStat2Label: 'SaySomethingin pupils scored in the top 10% of possible marks',
  scRfStat3Big: 'Every ability',
  scRfStat3Label: 'some of the strongest results came from pupils no one expected',

  // --- why does it work? (answer) ---
  scAnsKicker: 'Why does it work?',
  scAnsTitle: 'This daily speaking practice builds new synapses for all the pupils who take part',
  scAnsLede: 'A few minutes of spoken Italian every day, as a class. The language stops being something pupils study and becomes something they can use.',
  scAnsTag1: 'Speaking, not just reading',
  scAnsT1: 'They use it out loud, every day.',
  scAnsB1: "Audio prompts, thinking time and speaking out loud build the reflex to actually talk – the missing ingredient that doesn't happen in ordinary lessons.",
  scAnsTag2: 'Every ability',
  scAnsT2: 'Everyone speaks at once, so no one performs alone.',
  scAnsB2: "When the whole class answers together, it can get a little magical - as if they're all at a concert, and their brains are synchronising - it becomes a team game.",
  scAnsTag3: 'You can see it',
  scAnsT3: 'See the class moving.',
  scAnsB3: 'Run sessions together and watch confidence grow week by week. If individual accounts have been activated, pupils show up individually on your dashboard – practice, belts, progress – so you can spot who needs a nudge.',
  scAnsCloser: 'Same timetable. Same teacher. A language that starts to make sense to pupils.',

  // --- what actually happens (mechanism) ---
  scMechKicker: 'What actually happens',
  scMechTitle: 'Five minutes, out loud, every day.',
  scMechLede: 'No worksheets, no vocab lists, no marking. A session is a simple loop that gets every pupil producing spoken Italian from the first minute.',
  scMechStep1T: 'Hear it.',
  scMechStep1B: 'A voice asks for a phrase in English: "Say \'I want to speak.\'"',
  scMechStep2T: 'Reach for it.',
  scMechStep2B: "A pause. Each pupil's brain works to build the Italian – and that effort is exactly what makes it stick.",
  scMechStep3T: 'Say it.',
  scMechStep3B: 'They say it out loud. Wrong is fine; wrong then corrected is how the brain wires it in.',
  scMechStep4T: 'Hear it back.',
  scMechStep4B: 'Two Italian speakers say the phrase – "Voglio parlare" – and every pupil hears instantly how close they got.',
  scMechMaths: "Then the loop repeats, and the phrases grow and combine. And here's the part that changes everything: in an ordinary lesson, one pupil answers at a time. In a SaySomethingin session, every pupil in the room is speaking, the whole time. Five minutes a day can add up to more spoken Italian per pupil than a term of hands-up questions.",
  scMechCloser: "That's why five minutes a week moved the needle in the Cardiff study. Speaking is a physical skill, like swimming. Reading about it never made anyone a swimmer.",

  // --- your own Italian (teacher confidence) ---
  scConfKicker: 'Your own Italian',
  scConfTitle: 'Not confident in your Italian? You might be exactly the right teacher for this.',
  scConfBody1: "Running a session takes no fluency at all: log in, choose the class, press play. The session leads; you and the pupils respond. A substitute teacher can run it cold. If you're a primary teacher delivering languages on the back of a GCSE from years ago, this was built with you in mind.",
  scConfBody2: "And here's what the Cardiff research noticed in Wales: the classes where teachers said the Welsh along with their pupils showed better results. When pupils watch you reach for a phrase, miss, and try again, you're modelling the exact skill they need – and proving that mistakes are how speakers get made.",
  scConfCloser: "Shaky Italian isn't a weakness at the front of this classroom. It's a teaching asset. And in five minutes a day, you'll be learning too.",

  // --- languages (upsell) ---
  scLangKicker: 'More than Italian',
  scLangTitle: "Start with Italian. Add any language when you're ready.",
  scLangLede: 'The same method and the same dashboard work for every language we teach – so once Italian is running, adding Spanish or French for the rest of the department takes minutes, not meetings. One subscription covers them all.',
  scLangChipsLabel: 'Available now',
  scLangPoint1H: 'Proven on a hard case first',
  scLangPoint1B: 'SaySomethingin started with Welsh in 2009. Italian is one of the friendliest languages for English speakers – we learned our craft on one of the trickiest.',
  scLangPoint2H: 'Welsh & Irish, free for a year',
  scLangPoint2B: 'Teaching Welsh or Irish too? Both come with a full year free for your school – on the same dashboard, at no extra cost.',
  scLangPoint3H: 'English for new arrivals',
  scLangPoint3B: 'EAL pupils build spoken English the same way – daily, out loud, at their own pace.',
  scLangItalian: 'Italian',
  scLangWelsh: 'Welsh',
  scLangIrish: 'Irish',
  scLangSpanish: 'Spanish',
  scLangFrench: 'French',
  scLangGerman: 'German',
  scLangPortuguese: 'Portuguese',
  scLangMandarin: 'Mandarin',
  scLangJapanese: 'Japanese',
  scLangKorean: 'Korean',
  scLangArabic: 'Arabic',

  // --- how it works ---
  scHowKicker: 'How it works',
  scHowTitle: 'One method, three places it works.',
  scHowATag: 'In the classroom',
  scHowATitle: 'Play as a class',
  scHowA1: 'Run a session together on the whiteboard – one screen, every voice.',
  scHowA2: 'Use it as a five-minute starter, a full lesson, or cover work that teaches itself.',
  scHowA3: "Works on whatever's plugged into the board: the classroom computer, a Chromebook, a tablet. No special kit, no device fleet.",
  scHowBTag: 'Beyond the lesson',
  scHowBTitle: 'For pupils who want more',
  scHowB1: "Pupils with their own accounts practise at home, at their own pace – parents pay £5 a month through your school's link, never your budget.",
  scHowB2: 'Pupils who do this arrive at your next lessons warmed up, faster, and more willing to speak.',
  scHowCTag: 'For the school',
  scHowCTitle: 'See the whole picture',
  scHowC1: 'Class dashboards for every teacher; pupils with accounts show individual progress, belts and time on task.',
  scHowC2: 'Home accounts link to your class with a single code – no spreadsheets to maintain.',
  scHowC3: 'Roll it out across a class, a year group, or the whole school – it scales without extra admin.',
  scHowPitch: 'Set it up in an afternoon. See pupils speaking by the end of the week.',

  // --- built by teachers ---
  scBuiltKicker: 'Built by teachers',
  scBuiltTitle: "Shaped by people who've been at the front of the room.",
  scBuiltBody: 'SaySomethingin for schools was built by Tom and Aran, who are passionate classroom teachers. From the lesson flow to the way the dashboard works, every part was designed by people who know what a wet Friday afternoon with Year 8 actually feels like.',
  scBuiltName1: 'Tom',
  scBuiltRole1: 'Physics & Maths teacher',
  scBuiltImg1: '/assets/team/tom-c.jpg',
  scBuiltName2: 'Aran',
  scBuiltRole2: 'English & Welsh teacher',
  scBuiltImg2: '/assets/team/aran-j.jpg',

  // --- method (single statement) ---
  scMethodKicker: 'The method',
  scMethodTitle: 'No streaks. No shame.',
  scMethodBody: "No owls, no guilt loops, no leaderboards that humiliate the bottom half of the class. Mistakes are treated as the raw material of learning, because that's what they are. Pupils notice the difference.",
  scMethodMore: 'Read more about the methodology',

  // --- first movers ---
  scFirstKicker: 'First movers',
  scFirstTitle: 'Be one of the first schools to run it in Italian.',
  scFirstBody1: 'Our school results so far come from Welsh – which is exactly why the first Italian schools get a direct line to our team, close support, and a say in how the schools platform develops. Your free month is how we find out together what this does for your classes.',
  scFirstBody2: "In Wales, teachers at Chepstow School identified SaySomethingin provision as a significant contributing factor in their recent outstanding Estyn assessment. We'd like the next line on this page to be about your department.",
  scFirstCta: 'Talk to the schools team',

  // --- for heads of languages & headteachers ---
  scGovKicker: 'For heads of languages & headteachers',
  scGovTitle: 'Language teaching works better than anyone realises. But one piece is missing.',
  scGovBody1: 'For 17 years, we\'ve been noticing a consistent and interesting pattern in our adult learners. If they studied a language at school, they believe they learnt almost nothing. But when they start speaking - even in their thirties or forties - their school language comes back to life, and they discover that they can remember far more than they realised.',
  scGovBody2: 'Teachers, students and league tables all agree that most pupils leave school unable to speak the language they studied. But even a small amount of speaking practice can drive a new level of confidence that could change results for the better - and change how many pupils choose to carry a language on at all. The answer may already be mostly in place.',
  scGovStat: "Our projection, built on Cardiff University's findings in Wales: ten minutes a day from Year 7 would put pupils at a strong A2 within two school years - making B2 a realistic GCSE goal for all students.",
  scGovCta: 'Talk to the schools team',

  // --- sample & pricing (free month) ---
  scSampleKicker: 'Try it yourself',
  scSampleTitle: 'Try the method before your pupils do.',
  scSampleLede: 'Press play and learn for up to half an hour. Learn five sentences in a dozen languages over the weekend, and see how much you feel like showing off to your classes on Monday.',
  scSamplePickerLabel: 'Choose a language',
  scPricingKicker: 'Pricing',
  scPricingTitle: 'A month free. And pupils never cost the school a penny.',
  scPricingLede: "The classroom layer is free for your whole school for a month: every teacher runs daily whole-class sessions from the front. Then it's £15 per teacher a month, every language included. And for pupils who want more, parents can add an individual account – home practice, their own pace, per-pupil progress on your dashboard – for £5 a month through your school's link, 67% off the usual price.",
  scPricingOfferBig: 'A free month',
  scPricingOfferSub: 'of Italian, for your whole school',
  scPricingLine1Label: 'Classroom sessions (per teacher)',
  scPricingLine1Val: 'Free for a month, then £15 / mo',
  scPricingLine2Label: 'Pupil home accounts, paid by parents',
  scPricingLine2Val: '£5 / mo',
  scPricingFeat1: 'A full month of Italian free for your whole school',
  scPricingFeat2: 'Whole-class sessions on the board – pupils need no accounts',
  scPricingFeat3: 'Optional pupil accounts, paid by parents at 67% off: home practice and per-pupil progress',
  scPricingFeat4: '£15 per teacher a month after the free month – which unlocks every language we teach, Spanish to Mandarin, plus EAL English',
  scPricingFeat5: 'Up to 10 classes per teacher',
  scPricingCta: 'Claim your free month',
  scPricingNote: '*Per teacher, all languages included.',

  // --- FAQ (variety question deliberately absent — scFaqQ5 unused) ---
  scFaqKicker: 'School questions',
  scFaqTitle: 'The things you actually need to know.',
  scFaqLede: "Money, data, devices, and how pupils take part. If there's anything else you'd like to ask, email us and we'll come back within a working day.",
  scFaqContact: 'Email the schools team →',
  scFaqQ1: 'Do pupils need accounts?',
  scFaqA1: 'Not for class. You run sessions from the front and the whole class takes part – no logins, no setup. If a pupil wants their own account for home practice, your class code links it to your dashboard, so their progress shows up alongside your class.',
  scFaqQ2: 'What about pupil data and GDPR?',
  scFaqA2: "We collect the minimum we need to run the learning, store it securely, and never sell it. We're happy to sign a data-processing agreement, and pupils never need to give card details.",
  scFaqQ3: 'What devices does it need?',
  scFaqA3: 'One. Whatever already drives your whiteboard – the classroom computer, a Chromebook, a tablet. Cast the session to the board and the whole class speaks together. No trolleys, no logins, no special kit.',
  scFaqQ4: 'Which languages are included?',
  scFaqA4: 'This page is about Italian, but one subscription carries every language we teach: Welsh, Irish, Spanish, French, German, Portuguese, Mandarin, Japanese, Korean and Arabic, plus English for EAL pupils – £15 per teacher a month covers them all. And Welsh and Irish each come with a full year free.',
  scFaqQ6: 'Does it replace the teacher?',
  scFaqA6: 'Never. It does the repetition and drilling so you can spend lesson time on conversation, correction and culture – the things only you can do.',
  scFaqQ7: 'How does the teacher dashboard work?',
  scFaqA7: "You see your classes and their session history at a glance. Pupils with their own accounts appear individually – practice, belts and progress – so you can spot who needs a nudge and who's ready to be stretched. You can also play a session on the board straight from the dashboard.",
  scFaqQ8: 'Has this been tested in schools?',
  scFaqA8: "In Welsh, yes: an independent Cardiff University study found significant improvement from as little as five minutes a week. In Italian, you'd be among the first schools, and we'll be working particularly closely with the schools who are. Same engine, same loop, same science – and the free month is how you test it on your own classes.",
  scFaqQ9: 'Is it really free for a month?',
  scFaqA9: "Yes. Italian is free for your whole school for a full month, with no card needed to start. Pupils take part in class at no cost. The only payment is optional and doesn't touch your budget: parents can give their child the full app at home for £5 a month through your school's link. After the month, it's £15 per teacher a month – which includes every language we teach.",
  scFaqQ10: 'My own Italian is rusty. Is that a problem?',
  scFaqA10: "No – it might even help. Sessions run themselves, and Cardiff University's study in Wales noticed that classes where the teacher said the Welsh along with their pupils got better results. You don't need to be the expert in the room. You need to be the bravest learner in it.",

  // --- footer CTA ---
  scFooterCtaTitle: 'Give your pupils a language for life.',
  scFooterCtaSub: 'Set up in an afternoon, run your first session that day, and watch your pupils start to believe in themselves. A full month free, on us.',
  scFooterCtaBtn: 'Claim your free month',
  scFooterCtaBtn2: 'Talk to the schools team',
  scFooterCtaNote: 'Free for a month on Italian, for your whole school. No card details needed.',

  // --- shared chrome (Footer) ---
  footerCol1: 'The product',
  footerCol2: 'Community',
  footerCol3: 'Download',
  footerAbout: 'About',
  footerCareers: 'Careers',
  footerContact: 'Contact',
  footerHelp: 'Help centre',
  footerTerms: 'Terms',
  footerPrivacy: 'Privacy',
  footerAppHeading: 'Download',
  footerAppApple: 'iOS',
  footerAppGoogle: 'Android',
  footerLivingWage: 'We are a Living Wage Employer',
  footerCopyright: '© 2025 SaySomethingin Cyf',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Cross-link bar', keys: ['scCrossText'], long:[] },
  { name: 'Hero', keys: ['scKicker','scHeadline','scSub','scCta','scCta2','scTrust','scBadgeBig','scBadgeLabel'], long:['scHeadline','scSub','scBadgeLabel'] },
  { name: 'Cardiff study feature', keys: ['scRfKicker','scRfTitle','scRfBody','scRfHonest','scRfLink','scRfQuote','scRfCite','scRfStat1Big','scRfStat1Label','scRfStat2Big','scRfStat2Label','scRfStat3Big','scRfStat3Label'], long:['scRfTitle','scRfBody','scRfHonest','scRfQuote','scRfStat1Label','scRfStat2Label','scRfStat3Label'] },
  { name: 'Why does it work?', keys: ['scAnsKicker','scAnsTitle','scAnsLede','scAnsTag1','scAnsT1','scAnsB1','scAnsTag2','scAnsT2','scAnsB2','scAnsTag3','scAnsT3','scAnsB3','scAnsCloser'], long:['scAnsTitle','scAnsLede','scAnsB1','scAnsB2','scAnsB3','scAnsCloser'] },
  { name: 'What actually happens', keys: ['scMechKicker','scMechTitle','scMechLede','scMechStep1T','scMechStep1B','scMechStep2T','scMechStep2B','scMechStep3T','scMechStep3B','scMechStep4T','scMechStep4B','scMechMaths','scMechCloser'], long:['scMechLede','scMechStep1B','scMechStep2B','scMechStep3B','scMechStep4B','scMechMaths','scMechCloser'] },
  { name: 'Your own Italian', keys: ['scConfKicker','scConfTitle','scConfBody1','scConfBody2','scConfCloser'], long:['scConfTitle','scConfBody1','scConfBody2','scConfCloser'] },
  { name: 'Languages', keys: ['scLangKicker','scLangTitle','scLangLede','scLangChipsLabel','scLangPoint1H','scLangPoint1B','scLangPoint2H','scLangPoint2B','scLangPoint3H','scLangPoint3B','scLangItalian','scLangWelsh','scLangIrish','scLangSpanish','scLangFrench','scLangGerman','scLangPortuguese','scLangMandarin','scLangJapanese','scLangKorean','scLangArabic'], long:['scLangTitle','scLangLede','scLangPoint1B','scLangPoint2B','scLangPoint3B'] },
  { name: 'How it works', keys: ['scHowKicker','scHowTitle','scHowATag','scHowATitle','scHowA1','scHowA2','scHowA3','scHowBTag','scHowBTitle','scHowB1','scHowB2','scHowCTag','scHowCTitle','scHowC1','scHowC2','scHowC3','scHowPitch'], long:['scHowA1','scHowA2','scHowA3','scHowB1','scHowB2','scHowC1','scHowC2','scHowC3','scHowPitch'] },
  { name: 'Built by teachers', keys: ['scBuiltKicker','scBuiltTitle','scBuiltBody','scBuiltName1','scBuiltRole1','scBuiltImg1','scBuiltName2','scBuiltRole2','scBuiltImg2'], long:['scBuiltTitle','scBuiltBody'] },
  { name: 'Method', keys: ['scMethodKicker','scMethodTitle','scMethodBody','scMethodMore'], long:['scMethodTitle','scMethodBody'] },
  { name: 'First movers', keys: ['scFirstKicker','scFirstTitle','scFirstBody1','scFirstBody2','scFirstCta'], long:['scFirstTitle','scFirstBody1','scFirstBody2'] },
  { name: 'Heads of languages', keys: ['scGovKicker','scGovTitle','scGovBody1','scGovBody2','scGovStat','scGovCta'], long:['scGovTitle','scGovBody1','scGovBody2','scGovStat'] },
  { name: 'Sample & pricing', keys: ['scSampleKicker','scSampleTitle','scSampleLede','scSamplePickerLabel','scPricingKicker','scPricingTitle','scPricingLede','scPricingOfferBig','scPricingOfferSub','scPricingLine1Label','scPricingLine1Val','scPricingLine2Label','scPricingLine2Val','scPricingFeat1','scPricingFeat2','scPricingFeat3','scPricingFeat4','scPricingFeat5','scPricingCta','scPricingNote'], long:['scSampleTitle','scSampleLede','scPricingLede','scPricingFeat1','scPricingFeat3','scPricingFeat4','scPricingNote'] },
  { name: 'FAQ', keys: ['scFaqKicker','scFaqTitle','scFaqLede','scFaqContact','scFaqQ1','scFaqA1','scFaqQ2','scFaqA2','scFaqQ3','scFaqA3','scFaqQ4','scFaqA4','scFaqQ6','scFaqA6','scFaqQ7','scFaqA7','scFaqQ8','scFaqA8','scFaqQ9','scFaqA9','scFaqQ10','scFaqA10'], long:['scFaqLede','scFaqA1','scFaqA2','scFaqA3','scFaqA4','scFaqA6','scFaqA7','scFaqA8','scFaqA9','scFaqA10'] },
  { name: 'Footer CTA', keys: ['scFooterCtaTitle','scFooterCtaSub','scFooterCtaBtn','scFooterCtaBtn2','scFooterCtaNote'], long:['scFooterCtaTitle','scFooterCtaSub','scFooterCtaNote'] },
];

const CROSS_LANGS = [
  { label:'Welsh',   href:'/schools1' },
  { label:'Irish',   href:'/schools/irish' },
  { label:'Spanish', href:'/schools/spanish' },
  { label:'French',  href:'/schools/french' },
  { label:'German',  href:'/schools/german' },
  { label:'English', href:'/schools/english' },
];

export default function SchoolsItalian() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Schools — Italian">
      <Header t={tweaks}/>
      <SchoolsCrossLink t={tweaks} langs={CROSS_LANGS}/>
      <main>
        <SchoolsHero t={tweaks}/>
        <SchoolsResearchFeature t={tweaks}/>
        <SchoolsAnswer t={tweaks}/>
        <SchoolsMechanism t={tweaks}/>
        <SchoolsConfidence t={tweaks}/>
        <SchoolsLanguages t={tweaks} firstFlag="italian"/>
        <SchoolsHow t={tweaks}/>
        <SchoolsBuilt t={tweaks}/>
        <SchoolsMethod t={tweaks}/>
        <SchoolsFirst t={tweaks}/>
        <SchoolsGov t={tweaks}/>
        <SchoolsSample t={tweaks}/>
        <SchoolsFAQ t={tweaks}/>
        <SchoolsFooterCTA t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
